<template>
    <div v-show="store.state.showingModal === 'new'" class="b3-dialog-box">
        <div class="form-holder">
            <h1 class="themed-title">{{ t('new project title','New Project') }}</h1>
            <div class="b3-form-option">
                <label>{{t('project title text input','Project Title')}}</label>
                <input v-model="title" type="text" maxlength="100" :placeholder="t('enter title placeholder', 'Enter title here')">
            </div>
            <div class="b3-form-option">
                <label>{{caps(t('description','Description'))}}</label>
                <textarea v-model="description" :placeholder="t('enter description placeholder', 'Enter description here')"></textarea>
            </div>
            <div class="b3-form-option">
                <label>
                    {{t('bead library selection','Bead Library')}}
                    <span class="tooltip">&nbsp;ⓘ<span class="tooltiptext">{{t('bead library selection tip','Choose the beads you will work with.')}}</span></span>
                </label>
                <select v-model="libraryDisplayName">
                    <option
                        v-for="(element, index) in libraries" :key="index"
                        :value="element.displayName"
                    >
                        {{ element.displayName}}
                    </option>
                </select>
            </div>
            <div class="b3-form-option">
                <label>
                    {{t('pattern selection','Pattern')}}
                    <span class="tooltip">&nbsp;ⓘ<span class="tooltiptext">{{t('pattern selection tip','Choose the layout of the beads.')}}</span></span>
                </label>
                <select v-model="patternDisplayName">
                    <option
                        v-for="(element, index) in patterns" :key="index"
                        :value="element.displayName"
                        v-show="libraryDisplayName === 'Fuse Beads' ? element.plastic: !element.plastic"
                    >
                        {{ element.displayName}}
                    </option>
                </select>
            </div>
            <div class="b3-form-option" v-show="loggedIn">
                <label>{{t('collection','Collection')}}</label>
                <select v-model="chosenCollection">
                    <option
                    v-for="(element, index) in myCollections" :key="index"
                        :value="element.title"
                    >
                        {{ element.title}}
                    </option>
                </select>
            </div>
            <div class="b3-form-option" v-show="!chosenPattern.noColumns">
                <label>{{t('rows number input','Rows')}}</label>
                <input v-model="rows" type="number" min="1" max="400" >
            </div>
            <div class="b3-form-option" v-show="!chosenPattern.noColumns">
                <label>{{t('columns number input','Columns')}}</label>
                <input v-model="columns" type="number" min="1" max="400">
            </div>
            <div class="b3-form-option" v-show="chosenPattern.noColumns">
                <label>{{t('size number input','Size')}}</label>
                <input v-model="size" type="number" min="1" :max="chosenPattern.rowLimit || 30">
            </div>
            <div v-show="!chosenPattern.noCustomBeadSize">
                <div class="b3-form-option">
                    <label>{{t('use custom bead size','Use custom bead size')}}</label>
                    <input v-model="showAdvancedOptions" type="checkbox" >
                </div>
                <div class="my-highlighted-options" v-show="showAdvancedOptions">
                    <div class="b3-form-option">
                        <label>{{t('bead diameter number input','Bead diameter (mm)')}}</label>
                        <input v-model="diameter" type="number" min="0" max="50" step="0.01">
                    </div>
                    <div class="b3-form-option">
                        <label>{{t('bead width number input','Bead width (mm)')}}</label>
                        <input v-model="width" type="number" min="0" max="50" step="0.01">
                    </div>
                    <div>
                        <span>{{t('aspect ratio','Aspect ratio:')}}&nbsp;</span>
                        <span>{{ beadDimensions.aspectRatio.toFixed(2) }}</span>
                    </div>
                    <div class="aspect-ratio-warning" v-show="showAspectRatioWarning">
                        {{t('aspect ratio warning','Bead width must be between 50-100% of bead diameter.')}}
                    </div>
                </div>
            </div>
            <div class="b3-form-option">
                <label>{{t('project size title','Project size')}}</label>
                <span>
                    {{ cmToInches(projectWidth) }} x {{ cmToInches(projectHeight) }} {{ t('inches') }}
                </span>
            </div>
            <div class="b3-form-option">
                <label></label>
                <span>
                    {{ projectWidth }} x {{ projectHeight }} {{ t('cm') }}
                </span>
            </div>
            <div v-if="rows * columns > 60000" class="warning">
                {{t('size warning','⚠️ This will be a large project. Beadographer may run slowly, depending on device memory.')}}
            </div>
            <div class="launch-button-area">
                <button class="success-button" @click="launch">{{ t('launch project button', 'Launch') }}</button>
                <button class="quiet-button" @click="cancel">{{ t('cancel', 'Cancel') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import { libraries } from '../../myapp/src/libraries.js';
import { patterns } from '../../myapp/src/patterns/patterns.js';
import { start } from '../../myapp/src/core/start.js';
import { caps, lang } from '../../myapp/src/lang';
import { utils } from '../../myapp/src/utils';
import { menu } from '../../myapp/src/menu';

export default {
    data() {
        return {
            libraries: libraries,
            patterns: patterns.arr,
            showSizeWarning: false,
            libraryDisplayName: 'Miyuki Delica 11/0',
            patternDisplayName: 'Loom',
            chosenCollection: 'Default palette',
            rows: 30,
            columns: 30,
            size: 5,
            title: '',
            description: '',
            width: 1.23,
            diameter: 1.6,
            showAdvancedOptions: false,
            loggedIn
        }
    },
    computed: {
        myCollections: function () {
            return [{title: 'Default palette'}].concat(this.store.state.collections);
        },
        chosenPattern: function () {
            return this.patterns.find(element => element.displayName === this.patternDisplayName);
        },
        chosenLibrary: function () {
            return this.libraries.find(element => element.displayName === this.libraryDisplayName);
        },
        myLayout: function () {
            return patterns.getRowsAndColumns({
                pattern: this.chosenPattern,
                rows: this.rows,
                columns: this.columns,
                size: this.size
            });
        },
        beadDimensions: function () {
            const diameter = this.showAdvancedOptions ?
                this.diameter :
                this.chosenLibrary.diameter;

            const width = this.showAdvancedOptions ?
                this.width :
                this.chosenLibrary.width

            const aspectRatio = diameter / width;

            return { diameter, width, aspectRatio }
        },
        showAspectRatioWarning: function () {
            return (this.beadDimensions.aspectRatio < 1 || (this.beadDimensions.aspectRatio > 2));
        },
        dimensions: function () {
            return this.chosenPattern.getDimensions(
                this.myLayout.rows,
                this.myLayout.columns,
                this.beadDimensions.diameter,
                this.beadDimensions.width
            );
        },
        projectWidth: function () {
            return this.dimensions.width.toFixed(2);
        },
        projectHeight: function () {
            return this.dimensions.height.toFixed(2);
        },
        ignoreLastRow: function () {
            if (this.chosenPattern.name.slice(0, 6) !== 'peyote') return false;
            return this.rows % 2 === 1;
        }
    },
    watch: {
        libraryDisplayName() {
            if (this.libraryDisplayName === 'Fuse Beads' && !this.chosenPattern.plastic) {
                this.patternDisplayName = 'Square';
            }
            if (this.libraryDisplayName !== 'Fuse Beads' && this.chosenPattern.plastic) {
                this.patternDisplayName = 'Loom';
            }
        }
    },
    methods: {
        caps,
        cmToInches(num) {
            return (Number(num) / 2.54).toFixed(2);
        },
        resetForm() {
            this.libraryDisplayName = 'Miyuki Delica 11/0';
            this.patternDisplayName = 'Loom';
            this.rows = 30;
            this.columns = 30;
            this.size = 5;
            this.title = '';
            this.description = '';
            this.width = 1.23;
            this.diameter = 1.6;
            this.showAdvancedOptions = false;
        },
        launch() {
            start.newProject({
                title: this.title || lang.get('default project name', 'Untitled Project'),
                description: this.description,
                instructions: '',
                credits: '',
                tags: [],
                diameter: this.beadDimensions.diameter,
                width: this.beadDimensions.width,
                rows: this.myLayout.rows,
                columns: this.myLayout.columns,
                ignoreLastRow: this.ignoreLastRow,
                beadDisplayHeight: utils.getBeadDisplayHeight({aspectRatio: this.beadDimensions.aspectRatio}),
                library: this.chosenLibrary,
                pattern: this.chosenPattern,
                collection: this.chosenCollection,
                keepImage: false,
                loading: false,
                offset: null,
                camera: null,
                renameColors: false
            });

            menu.returnToCanvas();
        },
        cancel() {
            b3.store.mySet('showingModal', null);
        }
    },
    mounted() {
        this.ee.on('resetNewProjectModal', () => {
            this.resetForm();
        })
    }
}
</script>

<style lang="scss">
@import '../../assets/styles.scss';

.my-highlighted-options {
    padding: 5px 10px 10px 10px;
    background-color: white;
}
.aspect-ratio-warning {
    color: $warning;
    font-weight: bold;
}
.form-holder {
    background-color: $off_white;
    padding: 1px 20px 20px;
    width: 500px;
    margin: auto;
    border-radius: 5px;
    position: relative;
    top: 20px;
    max-height: calc(100vh - 60px);
    overflow-y: auto;
}
.launch-button-area {
    button {
        margin-right: 20px;
    }
}

</style>